/* lib/pushSubscription.js — Web Push lifecycle for ChainFlow OS.
 *
 * Responsibilities:
 *   • Fetch the server VAPID public key (cached for the session)
 *   • Ask Notification permission only when the user opts in — never on
 *     load, never modally
 *   • Subscribe via PushManager and ship the subscription to the server
 *   • Unsubscribe locally + server-side
 *   • Auto-resubscribe (silent) when permission is already granted and
 *     no subscription exists in the browser (e.g. cleared site data)
 *
 * Discipline:
 *   • No noisy logs to console in production paths
 *   • All errors are swallowed quietly except for the one we return to
 *     the caller (the affordance UI surfaces it calmly)
 */

import { api } from "./api";

const PUSH_PROMPTED_KEY = "cf:push-prompted-at";
const PUSH_DECLINED_KEY = "cf:push-declined";

let _publicKeyCache = null;

const urlBase64ToUint8Array = (base64String) => {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
};

const arrayBufferToBase64 = (buffer) => {
  const bytes = new Uint8Array(buffer);
  let bin = "";
  for (let i = 0; i < bytes.byteLength; i += 1) bin += String.fromCharCode(bytes[i]);
  return window.btoa(bin);
};

export const isPushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

export const currentPermission = () => {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission;
};

export const wasPushDeclined = () => {
  try { return localStorage.getItem(PUSH_DECLINED_KEY) === "1"; } catch { return false; }
};

export const markPushDeclined = () => {
  try { localStorage.setItem(PUSH_DECLINED_KEY, "1"); } catch { /* noop */ }
  try { localStorage.setItem(PUSH_PROMPTED_KEY, String(Date.now())); } catch { /* noop */ }
};

export const clearPushDeclined = () => {
  try { localStorage.removeItem(PUSH_DECLINED_KEY); } catch { /* noop */ }
};

const fetchPublicKey = async () => {
  if (_publicKeyCache) return _publicKeyCache;
  try {
    const { data } = await api.get("/api/push/public-key");
    if (data && data.enabled && data.public_key) {
      _publicKeyCache = data.public_key;
      return _publicKeyCache;
    }
  } catch {
    /* noop — surfaced as unsupported by the caller */
  }
  return null;
};

export const getServerEnabled = async () => {
  const key = await fetchPublicKey();
  return Boolean(key);
};

const subscriptionToJSON = (sub) => {
  const json = sub.toJSON ? sub.toJSON() : null;
  if (json && json.endpoint && json.keys) return json;
  // Manual fallback for older Safari quirks.
  return {
    endpoint: sub.endpoint,
    keys: {
      p256dh: arrayBufferToBase64(sub.getKey("p256dh")),
      auth:   arrayBufferToBase64(sub.getKey("auth")),
    },
  };
};

/**
 * subscribeToPush()
 * Returns:
 *   { ok: true }                 — subscribed (or already subscribed)
 *   { ok: false, reason }        — `reason` is one of:
 *      unsupported / denied / dismissed / server_disabled / error
 */
export const subscribeToPush = async () => {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  const publicKey = await fetchPublicKey();
  if (!publicKey) return { ok: false, reason: "server_disabled" };

  // Ask permission only if we haven't been granted yet.
  if (Notification.permission === "default") {
    try {
      const result = await Notification.requestPermission();
      try { localStorage.setItem(PUSH_PROMPTED_KEY, String(Date.now())); } catch { /* noop */ }
      if (result === "denied") {
        markPushDeclined();
        return { ok: false, reason: "denied" };
      }
      if (result !== "granted") return { ok: false, reason: "dismissed" };
    } catch {
      return { ok: false, reason: "error" };
    }
  }
  if (Notification.permission !== "granted") {
    return { ok: false, reason: Notification.permission };
  }

  try {
    const reg = await navigator.serviceWorker.ready;
    let sub = await reg.pushManager.getSubscription();
    if (!sub) {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      });
    }
    const json = subscriptionToJSON(sub);
    await api.post("/api/push/subscribe", json);
    clearPushDeclined();
    return { ok: true };
  } catch (err) {
    return { ok: false, reason: "error", error: err };
  }
};

export const unsubscribeFromPush = async () => {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  try {
    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) {
      markPushDeclined();
      return { ok: true };
    }
    const endpoint = sub.endpoint;
    try { await sub.unsubscribe(); } catch { /* noop */ }
    try { await api.post("/api/push/unsubscribe", { endpoint }); } catch { /* noop */ }
    markPushDeclined();
    return { ok: true };
  } catch {
    return { ok: false, reason: "error" };
  }
};

/** Silent re-subscribe if the user already granted permission. Call this
 *  on login so we restore the subscription after site-data clears or
 *  device migrations.
 */
export const refreshSubscriptionSilently = async () => {
  if (!isPushSupported()) return { ok: false, reason: "unsupported" };
  if (Notification.permission !== "granted") return { ok: false, reason: "no_permission" };
  if (wasPushDeclined()) return { ok: false, reason: "user_declined" };
  return subscribeToPush();
};

export const sendTestPush = async () => {
  try {
    const { data } = await api.post("/api/push/test", {});
    return { ok: true, ...data };
  } catch (err) {
    return { ok: false, error: err };
  }
};
